import type { MenuItem, SidebarSection } from '@tyndo/ui-kit';

/**
 * Pure helpers behind the sidebar's nav filter box and the persisted
 * per-group (ISA-95 section) collapse. Kept free of React/DOM so the
 * `node --test` sweep can pin the matching/persistence contracts.
 */

/**
 * Case-insensitive substring filter over the sidebar sections.
 *
 * Matching semantics:
 *  - an item whose label matches keeps its ENTIRE subtree (so matching a
 *    module name shows all of its views);
 *  - an item that doesn't match but has matching descendants is kept with
 *    only the matching branches (so matching a view name keeps the module
 *    row as its context);
 *  - sections with no surviving items are dropped;
 *  - an empty/whitespace query returns the input unchanged.
 */
export function filterSidebarSections(
  sections: SidebarSection[],
  query: string,
): SidebarSection[] {
  const q = query.trim().toLowerCase();
  if (!q) return sections;

  const filterItem = (item: MenuItem): MenuItem | null => {
    if (item.label.toLowerCase().includes(q)) return item;
    const children = (item.children ?? [])
      .map(filterItem)
      .filter((c): c is MenuItem => c !== null);
    if (children.length > 0) return { ...item, children };
    return null;
  };

  return sections
    .map((section) => ({
      ...section,
      items: section.items
        .map(filterItem)
        .filter((i): i is MenuItem => i !== null),
    }))
    .filter((section) => section.items.length > 0);
}

/** Toggle an id's membership in an immutable string list. */
export function toggleId(ids: readonly string[], id: string): string[] {
  return ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id];
}

// Versioned + tenant-scoped storage key for the collapsed sidebar groups —
// same shape as the sibling `tyndo.sidebar.v2.collapsed` rail key so one
// tenant's collapse preference never leaks into another's session on a
// shared workstation.
const COLLAPSED_GROUPS_KEY_PREFIX = 'tyndo.sidebar.v2.groups';

export const collapsedGroupsKeyFor = (
  tenantId: string | null | undefined,
): string => `${COLLAPSED_GROUPS_KEY_PREFIX}.${tenantId ?? 'shared'}`;

/** Read the persisted collapsed-group ids; storage failures read as none. */
export function readCollapsedGroups(key: string): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((x): x is string => typeof x === 'string')
      : [];
  } catch {
    return [];
  }
}

/** Persist the collapsed-group ids; storage failures are ignored. */
export function writeCollapsedGroups(key: string, ids: readonly string[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(ids));
  } catch {
    /* ignore — storage may be unavailable (private mode / blocked) */
  }
}
